import { monthNames } from "../../../../utils/constants";
import { CONFIG_LABELS } from "../institutionMerge.constants";
import { keyOf } from "./userFieldControl";

/*
 * What is still to decide on one wizard step — the SINGLE source of truth shared by the step
 * badges (Steps header), the footer counter, the StepGuide list and the rows of the dependency
 * steps, so they never disagree. Every item is `{ key, label, match? }`; the label is what the
 * operator reads (a field, an e-mail, a duplicate group, an address, a blocker).
 *
 * The local rules mirror the server readiness (institution_merge_service.js › readiness). On top
 * of them, the server's own blockers (`merge.readiness.blockers`) are folded into the step they
 * belong to (by their prefix) whenever no local item already covers them (`match`): a rule the
 * front does not know yet still shows up on its step instead of only on « Appliquer ».
 */

/** Steps that carry decisions (or blockers) and therefore show a badge. */
export const DECISION_STEPS = [
  "config",
  "users",
  "addresses",
  "responsables",
  "blacklists",
  "preview",
  "apply",
];

/** Server blocker prefix → step. */
const BLOCKER_STEP = [
  [
    /^(Configuration|Règles de facturation|Nom de la nouvelle institution)/,
    "config",
  ],
  [/^Comptes/, "users"],
  [/^Adresses/, "addresses"],
  [/^Responsables/, "responsables"],
  [/^Listes? noires?/, "blacklists"],
];
export const blockerStep = text => {
  const hit = BLOCKER_STEP.find(([rx]) => rx.test(String(text)));
  return hit ? hit[1] : null;
};

const monthKey = b => `${b.year}-${String(b.month).padStart(2, "0")}`;
const monthText = mm => {
  const [y, m] = String(mm).split("-");
  return `${monthNames[Number(m) - 1] || m} ${y}`;
};

const fieldLabels = cfg => {
  const out = { ...CONFIG_LABELS };
  for (const m of (cfg && cfg.meta) || [])
    if (m && m.field && m.label) out[m.field] = m.label;
  return out;
};

// ------------------------------------------------------------------ accounts

/** Decided AFTER values of a group: `fields`, plus the legacy `roles` (still accepted by the server). */
export const decidedUserFields = d => ({
  ...(d && d.roles && d.roles.role !== undefined ? { role: d.roles.role } : {}),
  ...(d && d.roles && d.roles.subRoles ? { subRoles: d.roles.subRoles } : {}),
  ...((d && d.fields) || {}),
});

/**
 * The paths where the accounts of a duplicate group disagree. The server sends `differing`;
 * older previews do not: the values are then compared locally over the field list, and a
 * « rôles différents » flag always requires the role — the same computation for the step
 * (StepUsers) and for the counters.
 */
export const differingOf = (g, fieldsList) => {
  if (Array.isArray(g.differing)) return g.differing;
  const out = fieldsList
    ? fieldsList
        .filter(
          f =>
            new Set(
              (g.accounts || []).map(a => keyOf((a.fields || {})[f.path], f)),
            ).size > 1,
        )
        .map(f => f.path)
    : [];
  if (g.rolesDiffer && !out.includes("role")) out.push("role");
  return out;
};

const fieldsListOf = preview =>
  preview &&
  preview.users &&
  Array.isArray(preview.users.fields) &&
  preview.users.fields.length
    ? preview.users.fields
    : null;

/** A duplicate-account group is decided when its action, survivor and every differing field are settled (keep: a valid rename). */
export const userGroupPending = (g, dec, fieldsList) => {
  if (!dec || !["merge", "keep"].includes(dec.action)) return true;
  if (dec.action === "keep")
    return !(
      dec.rename &&
      dec.rename.accountId &&
      /@/.test(dec.rename.email || "")
    );
  const fields = decidedUserFields(dec);
  return (
    !(g.accounts || []).some(a => a._id === dec.survivorId) ||
    differingOf(g, fieldsList).some(p => fields[p] === undefined)
  );
};

// ------------------------------------------------------------------ addresses / responsables

/** The labels the server uses in its blockers (institution_merge_preview.js) — used to match them. */
export const serverAddressLabel = a => {
  const head = [a.name, [a.street, a.number].filter(Boolean).join(" ")]
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(", ");
  const tail = [a.zip, a.city].filter(Boolean).join(" ");
  return (
    [head, tail].filter(Boolean).join(", ") ||
    (a.location || "").trim() ||
    `(adresse ${a.type || ""} sans libellé)`
  );
};
export const serverResponsableLabel = r =>
  `${r.firstname || ""} ${r.lastname || ""}`.trim() ||
  r.email ||
  "(responsable sans nom)";

const DEP_META = {
  addresses: {
    title: "Adresses",
    idsKey: "addressIds",
    name: serverAddressLabel,
  },
  responsables: {
    title: "Responsables",
    idsKey: "responsableIds",
    name: serverResponsableLabel,
  },
};

/** An address owned by an institution outside the merge (merely shared to a source): attach as it is, nothing else. */
export const isForeignAddress = a =>
  !!a && !!a.owner && !(a.owner.isSource || a.owner.isTarget);

const nonEmptyEdit = d =>
  !!(d && d.edit) &&
  typeof d.edit === "object" &&
  Object.keys(d.edit).length > 0;

/** Items, groups and decisions of one dependency section, indexed once. */
export const dependencyContext = (kind, preview, decisions) => {
  const section = (preview && preview[kind]) || {};
  const items = Array.isArray(section.items) ? section.items : [];
  const groups = Array.isArray(section.groups) ? section.groups : [];
  const idsKey = DEP_META[kind].idsKey;
  const groupOf = new Map();
  for (const g of groups) for (const id of g[idsKey] || []) groupOf.set(id, g);
  return {
    kind,
    items,
    groups,
    groupOf,
    byId: new Map(items.map(i => [i._id, i])),
    decs: (decisions && decisions[kind]) || {},
  };
};

/**
 * What the server readiness would refuse on one item, or null. Exactly its rules: a grouped
 * member needs an explicit keep / merge; a merge needs an `into` that is another item of the
 * merge, not itself merged away, in the same duplicate group; an outside-owned address can
 * neither merge nor be edited.
 */
export const dependencyItemProblem = (ctx, item) => {
  const dec = ctx.decs[item._id];
  const group = ctx.groupOf.get(item._id);
  const foreign = ctx.kind === "addresses" && isForeignAddress(item);
  if (group && (!dec || !["keep", "merge"].includes(dec.action)))
    return "garder ou fusionner ?";
  if (!dec) return null;
  if (dec.action === "merge") {
    if (foreign)
      return "adresse hors fusion — elle ne peut qu'être rattachée telle quelle";
    const into = dec.into && ctx.byId.get(dec.into);
    if (!into || dec.into === item._id) return "choisir dans quoi fusionner";
    if ((ctx.decs[dec.into] || {}).action === "merge")
      return "la cible est elle-même fusionnée";
    if (item.groupKey && into.groupKey !== item.groupKey)
      return "la cible doit appartenir au même doublon";
    return null;
  }
  if (dec.action && dec.action !== "keep") return "action invalide";
  if (foreign && nonEmptyEdit(dec))
    return "adresse hors fusion — elle ne peut pas être modifiée ici";
  return null;
};

const dependencyPending = (kind, preview, decisions) => {
  const ctx = dependencyContext(kind, preview, decisions);
  const meta = DEP_META[kind];
  const out = [];
  for (const item of ctx.items) {
    const problem = dependencyItemProblem(ctx, item);
    if (!problem) continue;
    const name = meta.name(item);
    // near-duplicates share their name: the owner tells them apart
    const owner = (item.owner || item.institution || {}).name;
    out.push({
      key: `${kind}.${item._id}`,
      label: `${name}${owner ? ` (${owner})` : ""} : ${problem}`,
      match: [name],
    });
  }
  return out;
};

// ------------------------------------------------------------------ steps

/** Local rules of one step (without the server blockers). */
function localPending(stepKey, preview, d) {
  const r = preview.decisionsRequired || {};
  switch (stepKey) {
    case "config": {
      const out = [];
      if (!(d.targetName || preview.existingTarget))
        out.push({
          key: "targetName",
          label: "Nom de la nouvelle institution",
          match: ["Nom de la nouvelle institution"],
        });
      const labels = fieldLabels(preview.config);
      const required =
        r.config || (preview.config && preview.config.required) || [];
      for (const f of required)
        if (!d.config || d.config[f] === undefined)
          out.push({
            key: `config.${f}`,
            label: labels[f] || f,
            match: [`« ${f} »`],
          });
      for (const mm of r.billings || [])
        if (
          !(Array.isArray(d.billings) ? d.billings : []).some(
            b => monthKey(b) === mm,
          )
        )
          out.push({
            key: `billings.${mm}`,
            label: `facturation ${monthText(mm)}`,
            match: [mm],
          });
      return out;
    }
    case "users": {
      const fieldsList = fieldsListOf(preview);
      return ((preview.users && preview.users.duplicates) || [])
        .filter(g =>
          userGroupPending(g, d.users && d.users[g.email], fieldsList),
        )
        .map(g => ({
          key: `users.${g.email}`,
          label: g.email,
          match: [` ${g.email} —`],
        }));
    }
    case "addresses":
    case "responsables":
      return dependencyPending(stepKey, preview, d);
    case "blacklists": {
      const names = new Map(
        (preview.blacklists || []).map(b => [String(b.interpreterId), b.name]),
      );
      return (r.blacklists || [])
        .filter(i => !(d.blacklists && d.blacklists[i]))
        .map(i => {
          const name = names.get(String(i)) || String(i);
          return { key: `blacklists.${i}`, label: name, match: [` ${name} —`] };
        });
    }
    default:
      return [];
  }
}

/**
 * Pending items of `stepKey`. `extra.readiness` is the server readiness (`merge.readiness`):
 * the whole list on « apply », the blockers of the step's own prefix on the decision steps.
 */
export function pendingOf(stepKey, preview, decisions, extra = {}) {
  if (!preview) return [];
  const d = decisions || {};
  const blockers = (extra.readiness && extra.readiness.blockers) || [];
  switch (stepKey) {
    case "preview":
      return (preview.blockers || []).map(b => ({
        key: `blocker.${b}`,
        label: b,
      }));
    case "apply":
      // two blockers can read the same (homonym addresses): the index keeps the keys unique
      return blockers.map((b, i) => ({ key: `readiness.${i}.${b}`, label: b }));
    default: {
      const local = localPending(stepKey, preview, d);
      const extraItems = blockers
        .filter(b => blockerStep(b) === stepKey)
        .filter(
          b => !local.some(it => (it.match || []).some(m => b.includes(m))),
        )
        .map((b, i) => ({ key: `readiness.${i}.${b}`, label: b }));
      return [...local, ...extraItems];
    }
  }
}

/** Every pending decision of the decision steps, labelled with its step — for the read-only steps and the apply step. */
export function pendingEverywhere(preview, decisions, titles = {}, extra = {}) {
  const out = [];
  for (const k of [
    "config",
    "users",
    "addresses",
    "responsables",
    "blacklists",
  ])
    for (const it of pendingOf(k, preview, decisions, extra))
      out.push({
        ...it,
        step: k,
        label: titles[k] ? `${titles[k]} : ${it.label}` : it.label,
      });
  return out;
}
